import { HasFormValue } from 'app/common/input/HasFormValue';
import { InputProps } from 'antd/lib/input';
import React, { CSSProperties, FocusEvent, KeyboardEvent } from 'react';
import { Input } from 'antd';
import Utils from 'app/common/util/Utils';

export interface InputFormatProps<T> extends HasFormValue<T>, Omit<InputProps, 'value' | 'defaultValue' | 'onChange'> {
    format: (value: T) => string;
    parse: (str: string) => T;
    matches: (str: string, complete: boolean) => boolean;
    defaultWidth?: string | number;
}

//Props interface that InputXXX components use.
export interface InputFormatWrapperProps<T>
    extends HasFormValue<T>,
        Omit<InputProps, 'value' | 'defaultValue' | 'onChange'> {}

export interface InputFormatState {
    value: string;
    warning?: boolean;
}

/**
 * A utility class for building InputXXX components that encode XXX objects into string format.
 */
export class InputFormat<T> extends React.PureComponent<InputFormatProps<T>, InputFormatState> {
    constructor(props) {
        super(props);
        this.state = {
            value: this.format(this.props.value),
        };
    }

    componentDidUpdate(prevProps, prevState): void {
        // If props.value changes, state must be updated accordingly
        // note : concerning null/undefined
        //   a) null is ignored, since it is used in onChange below (and we wish to maintain the current state.value)
        //   b) undefined is processed : could indicate a form has been reset/emptied.
        if (this.props.value !== null && prevProps.value !== this.props.value) {
            const valueFromProps = this.format(this.props.value);
            if (valueFromProps !== this.state.value) {
                this.setState({ value: valueFromProps });
            }
        }
    }

    format(value: T): string {
        if (this.props.format) {
            return this.props.format(value);
        } else if (typeof value === 'string') {
            return value;
        }
    }

    parse(str: string): T {
        if (this.props.parse) {
            return this.props.parse(str);
        } else if (typeof this.props.value === 'string') {
            return str as any as T; // T must be a string type.
        }
    }

    onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.currentTarget.value;
        let stateChanged: boolean = false;
        if (this.props.matches(inputValue, false)) {
            this.setState({ value: inputValue });
            stateChanged = true;
        }
        if (this.props.matches(inputValue, true)) {
            const newValue = this.parse(inputValue);
            this.props.onChange(newValue);
        } else if (this.props.value && stateChanged) {
            // user has probably hit the BACKSPACE button, so a previously valid input is now no longer fully valid.
            this.props.onChange(null); // clear value until user enters a fully correct string.
        }
    };

    /**
     * If the user leaves the Input (blur), or hits enter (Form submit), then we check the input is correctly formatted.
     * If not, then a warning look and feel will be applied.
     */
    setWarningIfNeeded() {
        this.setState(state => {
            const warning = Utils.isNotEmptyString(this.state.value) && !this.props.matches(this.state.value, true);
            if (warning !== state.warning) {
                return { warning: warning };
            }
        });
    }

    onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            this.setWarningIfNeeded();
        }
        this.props.onKeyPress?.(event);
    };

    onBlur = (event: FocusEvent<HTMLInputElement>) => {
        this.setWarningIfNeeded();
        this.props.onBlur?.(event);
    };

    onFocus = (event: FocusEvent<HTMLInputElement>) => {
        this.setState(state => {
            if (state.warning) {
                return {
                    warning: false,
                };
            }
        });
        this.props?.onFocus?.(event);
    };

    getUpdatedStyle(style: CSSProperties): CSSProperties {
        let newStyle = style;
        if (this.props.defaultWidth) {
            newStyle = { width: this.props.defaultWidth, ...style };
        }
        if (this.state.warning) {
            if (style === newStyle) {
                newStyle = { ...style, borderColor: '#faad14' };
            } else {
                newStyle.borderColor = '#faad14';
            }
        }
        return newStyle;
    }

    render() {
        const { matches, format, parse, style, defaultWidth, ...otherProps } = this.props;
        return (
            <Input
                {...otherProps}
                style={this.getUpdatedStyle(style)}
                value={this.state.value}
                onChange={this.onChange}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                onKeyPress={this.onKeyPress}
            />
        );
    }
}
